import React from 'react';
import { Card, Input, Button, Form, Spin, Image, Row, Col, Select, Divider } from 'antd';
import moment from 'moment';
import EventVideoAffiliateSyncCard from '@/components/EventVideoAffiliateSyncCard';

const { Option } = Select;

const EventScrapingSection = ({
  loading,
  onFinishEvent,
  onFinishTixrEvent,
  onFinishTickitEvent,
  eventData,
  showEventForm,
  setShowEventForm,
  // Default location filters
  countries,
  cities,
  defaultCountry,
  defaultCity,
  onDefaultCountryChange,
  onDefaultCityChange
}) => {
  return (
    <div>
      {/* Default Location Filter */}
      <Card title="Default Event Location" className="mb-4">
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Default Country">
              <Select
                placeholder="Select default country"
                value={defaultCountry}
                onChange={onDefaultCountryChange}
                allowClear
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
              >
                {countries?.map(country => (
                  <Option key={country._id} value={country._id}>
                    {country.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Default City">
              <Select
                placeholder="Select default city"
                value={defaultCity}
                onChange={onDefaultCityChange}
                allowClear
                disabled={!defaultCountry}
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
              >
                {cities?.map(city => (
                  <Option key={city._id} value={city._id}>
                    {city.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <div style={{ fontSize: '12px', color: '#888' }}>
          When set, all scraped events will automatically use these default country and city values.
        </div>
      </Card>

      <EventVideoAffiliateSyncCard />

      <Divider />

      {/* RA Event Scraper */}
      <Card title="RA Event Scraper" className="mb-4">
        <Form onFinish={onFinishEvent} layout="vertical">
          <Form.Item
            name="url"
            label="Resident Advisor Event URL"
            rules={[{ required: true, message: 'Please input the event URL!' }]}
          >
            <Input placeholder="https://ra.co/events/..." />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading}>
              Scrape RA Event
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {/* Tixr Event Scraper */}
      <Card title="Tixr Event Scraper" className="mb-4">
        <Form onFinish={onFinishTixrEvent} layout="vertical">
          <Form.Item
            name="tixrUrl"
            label="Tixr Event URL"
            rules={[{ required: true, message: 'Please input the Tixr event URL!' }]}
          >
            <Input placeholder="https://www.tixr.com/groups/..." />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading}>
              Scrape Tixr Event
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {/* Tickit Event Scraper */}
      <Card title="Tickit Event Scraper" className="mb-4">
        <Form onFinish={onFinishTickitEvent} layout="vertical">
          <Form.Item
            name="tickitUrl"
            label="Tickit Event URL"
            rules={[{ required: true, message: 'Please input the Tickit event URL!' }]}
          >
            <Input placeholder="https://{venue}.tickit.ca/events/..." />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading}>
              Scrape Tickit Event
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {loading && (
        <div className="text-center">
          <Spin size="large" />
          <p className="mt-2">Scraping event data...</p>
        </div>
      )}

      {eventData && !showEventForm && (
        <Card title="Scraped Event" className="mt-4" style={{ maxWidth: 400 }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {eventData.image && (
              <Image
                src={eventData.image}
                alt={eventData.name}
                style={{ maxHeight: 120, objectFit: 'cover', borderRadius: 8, marginBottom: 8 }}
                preview={false}
              />
            )}
            <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 4 }}>{eventData.name}</div>
            <div style={{ color: '#888', fontSize: 13, marginBottom: 4 }}>{eventData.dateTime}</div>
            <div style={{ color: '#888', fontSize: 13, marginBottom: 4 }}>{eventData.location || eventData.address}</div>
            {eventData.lineup && eventData.lineup.length > 0 && (
              <div style={{ fontSize: 13, marginBottom: 4 }}>
                <strong>Lineup:</strong> {eventData.lineup.join(', ')}
              </div>
            )}
            <Button type="primary" size="small" onClick={() => setShowEventForm(true)} style={{ marginTop: 8 }}>
              Create Event
            </Button>
          </div>
        </Card>
      )}

      {showEventForm && eventData && (
        <Row gutter={24}>
          <Col xs={24} md={8}>
            <Card title="Scraped Event" style={{ maxWidth: 400, marginBottom: 24 }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {eventData?.image && (
                  <Image
                    src={eventData.image}
                    alt={eventData.name}
                    style={{ maxHeight: 120, objectFit: 'cover', borderRadius: 8, marginBottom: 8 }}
                    preview={false}
                  />
                )}
                <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 4 }}>{eventData?.name}</div>
                <div style={{ color: '#888', fontSize: 13, marginBottom: 4 }}>
                  <strong>Start:</strong> {eventData?.dateTime || 'N/A'}
                </div>
                <div style={{ color: '#888', fontSize: 13, marginBottom: 4 }}>
                  <strong>End:</strong> {eventData?.endDateTime || (eventData?.dateTime ? moment(eventData.dateTime).add(6, 'hours').toISOString() : 'N/A')}
                </div>
                <div style={{ color: '#888', fontSize: 13, marginBottom: 4 }}>{eventData?.location || eventData?.address}</div>
                {eventData?.lineup && eventData.lineup.length > 0 && (
                  <div style={{ fontSize: 13, marginBottom: 4 }}>
                    <strong>Lineup:</strong> {eventData.lineup.join(', ')}
                  </div>
                )}
              </div>
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default EventScrapingSection;